import React, { useState, useEffect, useRef, useContext } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import '@/App.css';
import axios from 'axios';
import { toast } from 'sonner';
import { ThemeProvider } from './contexts/ThemeContext';
import { SponsorRotationProvider } from './context/SponsorRotationContext';
import RequireAuth from './components/RequireAuth';
import BaseCamp from './pages/BaseCamp';
import Browse from './pages/Browse';
import SubcategoryDetail from './pages/SubcategoryDetail';
import Circles from './pages/Circles';
import Timeline from './pages/Timeline';
import FriendsInbox from './pages/FriendsInbox';
import LocationPicker from './pages/LocationPicker';
import Home from './pages/Home';
import ServerDetail from './pages/ServerDetail';
import CreateServer from './pages/CreateServer';
import CreateGroup from './pages/CreateGroup';
import CategoryDetail from './pages/CategoryDetail';
import PostDetail from './pages/PostDetail';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import CreatePost from './pages/CreatePost';
// Note: Legacy Membership.js DELETED per cleanup directive
// New 3-Step Membership Flow (Intent → Explain → Payment)
import MembershipIntent from './pages/membership/MembershipIntent';
import MembershipExplain from './pages/membership/MembershipExplain';
import MembershipPayment from './pages/membership/MembershipPayment';
import MembershipBusiness from './pages/membership/MembershipBusiness';
import PaymentSuccess from './pages/PaymentSuccess';
import Messages from './pages/Messages';
import Profile from './pages/Profile';
import EventCalendar from './pages/EventCalendar';
import EventDetail from './pages/EventDetail';
import UserProfile from './pages/UserProfile';
import BusinessDashboard from './pages/BusinessDashboard';
import BusinessDirectory from './pages/BusinessDirectory';
import BusinessProfilePage from './pages/BusinessProfilePage';
import BusinessStats from './pages/BusinessStats';
import CreateAd from './pages/CreateAd';
import UpgradeBusiness from './pages/UpgradeBusiness';
import TermsOfService from './pages/TermsOfService';
import BusinessLanding from './pages/BusinessLanding';
// Referrals page removed - replaced with simple Invites in Settings
import ProfilePage from './pages/ProfilePage';
import LandingPage from './pages/LandingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AboutTerms from './pages/AboutTerms';
import Support from './pages/Support';
import ProfileSettings from './pages/ProfileSettings';
import Settings from './pages/Settings';
import Security from './pages/Security';
import MagicLinkVerify from './pages/MagicLinkVerify';
import BetaFeedback from './pages/BetaFeedback';
import BetaStatus from './pages/BetaStatus';
import BetaSurvey from './pages/BetaSurvey';
import AdminBetaDashboard from './pages/AdminBetaDashboard';
import ResetPassword from './pages/ResetPassword';
// Badges page removed per LikeKind spec - replaced with Placard system
import SubscriptionPlans, { SubscriptionSuccess, SubscriptionCancel } from './pages/SubscriptionPlans';
import EarningsSummary from './pages/EarningsSummary';
import AdminFinancePortal from './pages/AdminFinancePortal';
import BusinessVisibility from './pages/BusinessVisibility';
import BusinessBilling from './pages/BusinessBilling';
import AdminModerationDashboard from './pages/AdminModerationDashboard';
import ContentGuidelines from './pages/ContentGuidelines';
import TrustSafety from './pages/TrustSafety';
import TrustOverview from './pages/TrustOverview';
import FamilySafety from './pages/FamilySafety';
import FounderConsole from './pages/FounderConsole';
import FounderInfo from './pages/FounderInfo';
import FounderWelcomeModal from './components/FounderWelcomeModal';
import ParentVisibility from './pages/ParentVisibility';
import FounderStatement from './pages/FounderStatement';
import PhotosPage from './pages/PhotosPage';
import InvitesSettings from './pages/InvitesSettings';
import FAQ from './pages/FAQ';
import HowItWorks from './pages/HowItWorks';
import WhatIsLikeKind from './pages/WhatIsLikeKind';
import LocationSettings from './pages/LocationSettings';
import CommonsManagement from './pages/CommonsManagement';
import CommonsActivation from './pages/CommonsActivation';
import Scuta from './pages/Scuta';
import ScutaEntries from './pages/ScutaEntries';
import ScutaEntryDetail from './pages/ScutaEntryDetail';
import ParticipationHistory from './pages/ParticipationHistory';
import GroupProfile from './pages/GroupProfile';
import CommonsStart from './pages/CommonsStart';
import SupportFind from './pages/SupportFind';
import SupportSponsor from './pages/SupportSponsor';
import SupportIncoming from './pages/SupportIncoming';
import ExternalSupport from './pages/ExternalSupport';
import ScutaHome from './pages/ScutaHome';
import Calendar from './pages/Calendar';
import CommunityCalendar from './pages/CommunityCalendar';
import EditMyWorld from './pages/EditMyWorld';
import Contacts from './pages/Contacts';
import PhotosManager from './pages/PhotosManager';
import CategoryManager from './pages/CategoryManager';
import { Toaster } from './components/ui/sonner';

// SPONSORSHIP SYSTEM (LOCKED DIRECTIVE)
import SponsorPolicy from './pages/SponsorPolicy';
import SponsorProfile from './pages/SponsorProfile';
import BecomeSupporter from './pages/BecomeSupporter';
import QuickCreate from './pages/QuickCreate';
import FounderSponsorAdmin from './pages/FounderSponsorAdmin';

// POLICY & CONTACT SYSTEM (LOCKED DIRECTIVE)
import ContactUs from './pages/ContactUs';
import FounderPolicyLog from './pages/FounderPolicyLog';

// ACTIVITY SYSTEM (Phase 2)
import ActivityCreate from './pages/ActivityCreate';
import ActivityDetail from './pages/ActivityDetail';
import ActivityEdit from './pages/ActivityEdit';

// MAP SYSTEM (Phase 3)
import MapView from './pages/MapView';

// BACKPACK (Profile)
import Backpack from './pages/Backpack';
import { BackpackActivity, BackpackPeople, BackpackMemory, BackpackGroups, BackpackCommons } from './pages/BackpackSections';

// Simple Feedback Widget (V1 Beta)
import SimpleFeedback from './components/SimpleFeedback';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Root route: Base Camp for EVERYONE (guest or authed). Never land on Landing.
// Per "zero-friction entry" directive: the user opens the app and is already in.
function RootRedirect() {
  return <BaseCamp />;
}

// INSTITUTIONAL LAYER (Phase 4)
import InstitutionDashboard from './pages/InstitutionDashboard';
import DocumentCreate from './pages/DocumentCreate';
import DocumentDetail from './pages/DocumentDetail';

// Landing Pages (Public Entry Points - BUILD 1)
import { IndividualsLanding, FamiliesLanding, SchoolsLanding, InstitutionsLanding, CommonsLanding } from './pages/landing';
import LandingPageNew from './pages/LandingPageNew';

// Institution Pages (BUILD 1-6)
import { InstitutionCreate, InstitutionProfile, InstitutionDiscover } from './pages/institutions';

// FAQ Pages (BUILD 8)
import { FAQPage } from './pages/faq';

// School Pages (BUILD 4)
import { StudentNotebook, SchoolCalendar } from './pages/school';

// P0 NEXT BUILD PACK: Discovery & Public Profile
import PublicProfile from './pages/PublicProfile';
import SchoolsDiscovery from './pages/SchoolsDiscovery';
import InstitutionsDiscovery from './pages/InstitutionsDiscovery';

// Onboarding Pages (BUILD 8 + PART B)
import { WelcomeLetter, RoleOnboarding } from './pages/onboarding';

// Family Pages (PART C)
import { ParentDashboard, YouthAdultConversion } from './pages/family';

// Institution Messaging Pages (Phase 1)
import { ComposeMessage, MessageLog, CoordinationInbox } from './pages/messaging';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

// Global: send HttpOnly auth cookies on every request.
axios.defaults.withCredentials = true;

export const AuthContext = React.createContext();

// AuthCallback is now inside AppRouter to access AuthContext
function AuthCallbackInner({ setUser }) {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        navigate('/auth', { replace: true });
        return;
      }

      try {
        // Step 1: Exchange session_id for token
        const sessionRes = await axios.post(`${API}/api/auth/session`, {}, {
          headers: { 'X-Session-ID': sessionId },
          withCredentials: true
        });
        
        const token = sessionRes.data.token;
        if (!token) {
          throw new Error('No token received from session');
        }
        
        // Step 2: Store token in localStorage
        localStorage.setItem('token', token);
        
        // Step 3: Fetch current user with the new token (DURABLE - not navigation state)
        const meRes = await axios.get(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Step 4: Set user in context (IMMEDIATELY updates UI)
        setUser(meRes.data);
        
        // Step 5: Get redirect target and navigate
        const target = sessionStorage.getItem('lk_post_auth_redirect') || '/';
        sessionStorage.removeItem('lk_post_auth_redirect');
        
        navigate(target, { replace: true });
        
      } catch (error) {
        console.error('Auth callback error:', error);
        localStorage.removeItem('token');
        toast.error('Authentication failed. Please try again.');
        navigate('/auth', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        <p className="mt-4 text-slate-700">Completing authentication...</p>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [showFounderWelcome, setShowFounderWelcome] = useState(false);

  // Check if this is an OAuth callback (has session_id in hash)
  const isAuthCallback = location.hash?.includes('session_id=');

  useEffect(() => {
    // Skip auth check if this is an OAuth callback - AuthCallbackInner will handle it
    if (isAuthCallback) {
      return;
    }
    
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/api/auth/me`, {
          headers,
          withCredentials: true,
        });
        setUser(response.data);

        // Frictionless invite: if a pending invite was captured pre-auth, apply it now
        const pendingInvite = sessionStorage.getItem('lk_pending_invite');
        if (pendingInvite) {
          try {
            const res = await axios.post(
              `${API}/api/auth/apply-pending-invite`,
              { code: pendingInvite },
              { headers, withCredentials: true }
            );
            if (res.data?.applied) {
              toast.success(`You joined through ${res.data.inviter_name}`);
            }
          } catch {
            // silently ignore — do not block session start
          }
          sessionStorage.removeItem('lk_pending_invite');
        }

        if (response.data) {
          checkFounderWelcome(token);
        }
      } catch (error) {
        // Only clear the legacy token. Cookies remain — they may be valid for a different client.
        localStorage.removeItem('token');
      } finally {
        setAuthResolved(true);
      }
    };

    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation) {
      setLocationData(JSON.parse(savedLocation));
    }

    checkAuth();
  }, [isAuthCallback]);

  // Check if founder welcome should be shown — DISABLED for clean entry
  const checkFounderWelcome = async (token) => {
    // Welcome modal removed — users land directly in Base Camp
  };

  // Handle founder welcome dismissal
  const dismissFounderWelcome = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/user/founder-welcome/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowFounderWelcome(false);
    } catch (error) {
      console.error('Error dismissing founder welcome:', error);
    }
  };
  
  // Render AuthCallbackInner for OAuth callback (pass setUser for immediate state update)
  if (isAuthCallback) {
    return <AuthCallbackInner setUser={setUser} />;
  }

  // Zero-friction entry: no blocking loader. Render routes immediately; auth resolves silently in the background.
  // (Individual pages guard themselves via their own /api/auth/me calls if needed.)

  return (
    <AuthContext.Provider value={{ user, setUser, authResolved, locationData, setLocationData, setShowFounderWelcome }}>
      <SponsorRotationProvider>
        {/* Founder Welcome Modal - Full screen, appears once per account, only closes via X */}
        <FounderWelcomeModal open={showFounderWelcome} onClose={dismissFounderWelcome} />
        <ScrollToTop />
        <Routes>
        {/* ROOT: Map for authenticated users, Landing for guests */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="/landing" element={<LandingPage />} />
        {/* BASE CAMP is reached via explicit action from Landing Page */}
        <Route path="/base-camp" element={<BaseCamp />} />
        <Route path="/my-world" element={<BaseCamp />} />
        <Route path="/basecamp" element={<BaseCamp />} />
        {/* Browse is SECONDARY (discovery/filtering) */}
        <Route path="/browse" element={<Browse />} />
        <Route path="/category/:categoryId" element={<SubcategoryDetail />} />
        <Route path="/circles" element={<Circles />} />
        <Route path="/timeline" element={<Scuta />} />
        <Route path="/scuta" element={<ScutaHome />} />
        <Route path="/scuta/explore" element={<Scuta />} />
        <Route path="/scuta/entries" element={<ScutaEntries />} />
        <Route path="/scuta/entries/:entryId" element={<ScutaEntryDetail />} />
        <Route path="/participation" element={<ParticipationHistory />} />
        <Route path="/home" element={<Home />} />
        <Route path="/location" element={<LocationPicker />} />
        <Route path="/server/:serverId" element={<ServerDetail />} />
        <Route path="/create-server" element={<CreateServer />} />
        <Route path="/create-group" element={<CreateGroup />} />
        {/* ACTIVITY SYSTEM (Phase 2) */}
        <Route path="/activities/create" element={<RequireAuth><ActivityCreate /></RequireAuth>} />
        <Route path="/activities/:activityId/edit" element={<RequireAuth><ActivityEdit /></RequireAuth>} />
        <Route path="/activities/:activityId" element={<ActivityDetail />} />
        {/* MAP SYSTEM (Phase 3) */}
        <Route path="/map" element={<MapView />} />
        <Route path="/backpack" element={<Backpack />} />
        <Route path="/backpack/activity" element={<BackpackActivity />} />
        <Route path="/backpack/people" element={<BackpackPeople />} />
        <Route path="/backpack/memory" element={<BackpackMemory />} />
        <Route path="/backpack/groups" element={<BackpackGroups />} />
        <Route path="/backpack/commons" element={<BackpackCommons />} />
        {/* Commons Management - Dual Steward Rule */}
        <Route path="/commons/:commonsId/manage" element={<CommonsManagement />} />
        <Route path="/groups/:groupId/activate-commons" element={<CommonsActivation />} />
        <Route path="/category/:categoryId" element={<CategoryDetail />} />
        <Route path="/post/:postId" element={<PostDetail />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/magic" element={<MagicLinkVerify />} />
        {/* /dashboard route unrouted — legacy page retained on disk but not reachable */}
        <Route path="/create-post" element={<CreatePost />} />
        {/* NEW 3-STEP MEMBERSHIP FLOW — LOCKED DIRECTIVE */}
        {/* Screen 1: Intent - "Choose how to enter" */}
        <Route path="/membership" element={<MembershipIntent />} />
        {/* Business Membership - Separate flow with sponsorship alignment (MUST be before :type) */}
        <Route path="/membership/business" element={<MembershipBusiness />} />
        <Route path="/membership/business/payment" element={<MembershipPayment />} />
        {/* upgrade-business unrouted — legacy SaaS language */}
        {/* Screen 2: Explain - "What this membership is" (no pricing) */}
        <Route path="/membership/:type" element={<MembershipExplain />} />
        {/* Screen 3: Payment - pricing + cadence + promo + subscribe */}
        <Route path="/membership/:type/payment" element={<MembershipPayment />} />
        {/* Legacy membership route REMOVED - file deleted per cleanup directive */}
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/inbox" element={<FriendsInbox />} />
        {/* Legacy /profile removed - Base Camp is the primary identity surface */}
        <Route path="/events" element={<EventCalendar />} />
        <Route path="/events/:eventId" element={<EventDetail />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/community-calendar" element={<CommunityCalendar />} />
        <Route path="/user-profile" element={<UserProfile />} />
        <Route path="/business-dashboard" element={<BusinessDashboard />} />
        <Route path="/business-stats" element={<BusinessStats />} />
        <Route path="/create-ad" element={<CreateAd />} />
        {/* /upgrade-business unrouted — legacy SaaS upgrade language */}
        <Route path="/directory" element={<BusinessDirectory />} />
        <Route path="/business/:businessId" element={<BusinessProfilePage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/business" element={<BusinessLanding />} />
        {/* /referrals route removed - Invites now in Settings */}
        {/* P0: Public profile preview at /u/{handle} */}
        <Route path="/u/:handle" element={<PublicProfile />} />
        {/* Legacy profile routes for authenticated users */}
        <Route path="/profile/:userId" element={<ProfilePage />} />
        <Route path="/profile/:userId/photos" element={<PhotosPage />} />
        {/* P0: School and Institution Discovery Pages (read-only) */}
        <Route path="/schools" element={<SchoolsDiscovery />} />
        {/* /institutions registered below with the newer InstitutionDiscover */}
        {/* GROUP PROFILE — AUTO-GENERATED (LOCKED DIRECTIVE) */}
        <Route path="/group/:groupId" element={<GroupProfile />} />
        <Route path="/commons/start" element={<CommonsStart />} />
        <Route path="/support/find" element={<SupportFind />} />
        <Route path="/support/sponsor/:sponsorId" element={<SupportSponsor />} />
        <Route path="/support/requests" element={<SupportIncoming />} />
        <Route path="/support/external" element={<ExternalSupport />} />
        <Route path="/me" element={<BaseCamp />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/parent-visibility" element={<ParentVisibility />} />
        <Route path="/founder-statement" element={<FounderStatement />} />
        <Route path="/trust" element={<TrustOverview />} />
        <Route path="/family-safety" element={<FamilySafety />} />
        <Route path="/about-terms" element={<AboutTerms />} />
        <Route path="/support" element={<Support />} />
        <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
        <Route path="/settings/security" element={<RequireAuth><Security /></RequireAuth>} />
        <Route path="/settings/profile" element={<RequireAuth><ProfileSettings /></RequireAuth>} />
        <Route path="/settings/invites" element={<RequireAuth><InvitesSettings /></RequireAuth>} />
        <Route path="/settings/location" element={<RequireAuth><LocationSettings /></RequireAuth>} />
        <Route path="/settings/my-world" element={<RequireAuth><EditMyWorld /></RequireAuth>} />
        <Route path="/settings/photos" element={<RequireAuth><PhotosManager /></RequireAuth>} />
        <Route path="/settings/categories" element={<RequireAuth><CategoryManager /></RequireAuth>} />
        <Route path="/contacts" element={<RequireAuth><Contacts /></RequireAuth>} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/faq/:audience" element={<FAQPage />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/what-is-likekind" element={<WhatIsLikeKind />} />
        
        {/* PUBLIC ENTRY PAGES - BUILD 1 */}
        <Route path="/for/individuals" element={<IndividualsLanding />} />
        <Route path="/for/families" element={<FamiliesLanding />} />
        <Route path="/for/schools" element={<SchoolsLanding />} />
        {/* /for/institutions unrouted — legacy landing with "unlock/paid features" language */}
        <Route path="/for/commons" element={<CommonsLanding />} />
        <Route path="/welcome" element={<LandingPageNew />} />
        
        {/* INSTITUTION ROUTES - BUILD 1-6 */}
        <Route path="/institutions" element={<InstitutionDiscover />} />
        {/* /institutions/create unrouted — legacy creation flow with "unlock/paid features" language */}
        <Route path="/institutions/:institutionId" element={<InstitutionProfile />} />
        <Route path="/institutions/:institutionId/dashboard" element={<InstitutionDashboard />} />
        <Route path="/institutions/:institutionId/create-document" element={<DocumentCreate />} />
        <Route path="/documents/:documentId" element={<DocumentDetail />} />
        <Route path="/discover/institutions" element={<InstitutionDiscover />} />
        
        {/* SCHOOL ROUTES - BUILD 4 */}
        <Route path="/institutions/:institutionId/calendar" element={<SchoolCalendar />} />
        <Route path="/institutions/:institutionId/students/:studentId/notebook" element={<StudentNotebook />} />
        
        {/* INSTITUTION MESSAGING ROUTES - PHASE 1 */}
        <Route path="/institutions/:institutionId/compose" element={<ComposeMessage />} />
        <Route path="/institutions/:institutionId/messages" element={<MessageLog />} />
        <Route path="/institutions/:institutionId/messages/:messageId" element={<MessageLog />} />
        <Route path="/coordination-inbox" element={<CoordinationInbox />} />
        <Route path="/inbox" element={<CoordinationInbox />} />
        
        {/* ONBOARDING/WELCOME ROUTES - BUILD 8 */}
        <Route path="/welcome/:role" element={<WelcomeLetter />} />
        <Route path="/onboarding" element={<RoleOnboarding />} />
        
        {/* FAMILY ROUTES - PART C */}
        <Route path="/family" element={<ParentDashboard />} />
        <Route path="/family/dashboard" element={<ParentDashboard />} />
        <Route path="/family/convert" element={<YouthAdultConversion />} />
        {/* /beta/feedback and /beta/status unrouted — legacy premium-reward language */}
        <Route path="/beta/survey" element={<BetaSurvey />} />
        <Route path="/admin/beta" element={<AdminBetaDashboard />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* /badges route removed - Placard system replaces badges */}
        {/* /subscription, /subscription/success, /subscription/cancel unrouted — legacy SaaS billing flow */}
        <Route path="/earnings" element={<EarningsSummary />} />
        <Route path="/admin/finance" element={<AdminFinancePortal />} />
        <Route path="/admin/moderation" element={<AdminModerationDashboard />} />
        <Route path="/business/visibility" element={<BusinessVisibility />} />
        {/* /business/billing unrouted — legacy Stripe subscription dashboard */}
        <Route path="/content-guidelines" element={<ContentGuidelines />} />
        <Route path="/trust-safety" element={<TrustSafety />} />
        <Route path="/founder" element={<RequireAuth><FounderConsole /></RequireAuth>} />
        <Route path="/founder-info" element={<FounderInfo />} />
        
        {/* SPONSORSHIP SYSTEM — LOCKED DIRECTIVE */}
        <Route path="/sponsor-policy" element={<SponsorPolicy />} />
        <Route path="/sponsors/:sponsorId" element={<SponsorProfile />} />
        <Route path="/become-supporter" element={<BecomeSupporter />} />
        <Route path="/quick-create" element={<QuickCreate />} />
        <Route path="/founder/sponsors" element={<RequireAuth><FounderSponsorAdmin /></RequireAuth>} />
        
        {/* POLICY & CONTACT SYSTEM — LOCKED DIRECTIVE */}
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/founder/policy-log" element={<RequireAuth><FounderPolicyLog /></RequireAuth>} />
      </Routes>
      <Toaster position="top-right" richColors />
      {/* Simple Feedback Widget — V1 Beta */}
      <SimpleFeedback />
      </SponsorRotationProvider>
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;